import React, { useMemo, useState } from "react";
import { ContentContainer } from "../../../../components/layout";
import { Breadcrumbs, Button, Modal } from "../../../../components/core";
import { useParams } from "react-router-dom";
import { Button as PrimereactButton } from "primereact/button";
import { useQuery } from "@apollo/client";
import ViewSurveillancePhotosModal from "../modals/view-surveillance-photos";
import axios from "axios";
import { API } from "../../../../constants/api";
import RemoteOpenDoorModal from "../modals/remote-open-door-modal";
import UpdateRecipientMobileModal from "../modals/update-recipient-mobile-modal";
import { ProgressBar } from "primereact/progressbar";
import { useSelector } from "react-redux";
import { NEW_PACKAGE_HISTORY_QUERY_WITH_PAYMENT } from "../../../../constants/graphql/view-package-history-queries";
import { formatNumbers } from "..";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { formatDateTime } from "../../../../algorithms/date";
import RefundDialog from "../modals/refund-dialog";
import { Skeleton } from "primereact/skeleton";
import usePackageStateLabel from "../../../../utils/hooks/usePackageStateLabel";
// ─── Constants ────────────────────────────────────────────────────────────────
const PACKAGE_DETECTED = ["Unknown", "Package Detected"];

const BASE_STATES = {
  110: "Pending",
  118: "Removed",
  121: "Overstay",
  128: "Claimed",
  150: "Cancelled",
  158: "Cleared Out",
};

const WITHOUT_INTEGRATION_STATES = {
  130: "Take-Back",
  138: "Revoked",
};

const WITH_INTEGRATION_STATES = {
  140: "For RTS",
  141: "RTS Overstay",
  148: "RTS",
};

export const PARTNER_STATES = {
  300: "New",
  310: "Drop-Off",
  320: "Pending",
  328: "Claimed",
  350: "Cancelled",
};

export const PACKAGE_STATES = {
  with_integration: {
    ...WITH_INTEGRATION_STATES,
    ...BASE_STATES,
  },
  without_integration: {
    ...WITHOUT_INTEGRATION_STATES,
    ...BASE_STATES,
  },
};

// Flat map covering all states (backward compatible)
export const packageStates = {
  ...BASE_STATES,
  ...WITH_INTEGRATION_STATES,
  ...WITHOUT_INTEGRATION_STATES,
  ...PARTNER_STATES,
};

const services = ["Dropoff", "Shipout", "Storage"];

const breadcrumbItems = [
  { label: "View Package Table", path: `/view-package-history/` },
  { label: "Package History", path: "#" },
];

// ─── State tier mapping ───────────────────────────────────────────────────────
const STATE_TIER_MAP = {
  110: "pending",
  118: "negative",
  121: "info",
  128: "success",
  130: "warning",
  138: "revoked",
  140: "warning",
  141: "critical",
  148: "revoked",
  150: "negative",
  158: "cleared",
  300: "neutral",
  310: "dropoff",
  320: "pending",
  328: "success",
  350: "negative",
};

const TIER_STYLES = {
  neutral: {
    dot: "bg-gray-300",
    card: "bg-gray-50 border-gray-200",
    text: "text-gray-700",
    badge: "bg-gray-100 border-gray-300 text-gray-700",
  },
  pending: {
    dot: "bg-blue-500",
    card: "bg-blue-50 border-blue-200",
    text: "text-blue-800",
    badge: "bg-blue-100 border-blue-300 text-blue-800",
  },
  info: {
    dot: "bg-yellow-500",
    card: "bg-yellow-50 border-yellow-300",
    text: "text-yellow-800",
    badge: "bg-yellow-100 border-yellow-300 text-yellow-800",
  },
  warning: {
    dot: "bg-orange-500",
    card: "bg-orange-50 border-orange-300",
    text: "text-orange-900",
    badge: "bg-orange-100 border-orange-300 text-orange-900",
  },
  critical: {
    dot: "bg-red-500",
    card: "bg-red-50 border-red-300",
    text: "text-red-900",
    badge: "bg-red-100 border-red-300 text-red-900",
  },
  success: {
    dot: "bg-emerald-500",
    card: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-800",
    badge: "bg-emerald-100 border-emerald-300 text-emerald-800",
  },
  negative: {
    dot: "bg-slate-500",
    card: "bg-slate-50 border-slate-200",
    text: "text-slate-700",
    badge: "bg-slate-100 border-slate-300 text-slate-700",
  },
  revoked: {
    dot: "bg-red-700",
    card: "bg-red-100 border-red-400",
    text: "text-red-900",
    badge: "bg-red-100 border-red-400 text-red-900",
  },
  cleared: {
    dot: "bg-purple-500",
    card: "bg-purple-50 border-purple-200",
    text: "text-purple-800",
    badge: "bg-purple-100 border-purple-300 text-purple-800",
  },
  dropoff: {
    dot: "bg-sky-500",
    card: "bg-sky-50 border-sky-200",
    text: "text-sky-800",
    badge: "bg-sky-100 border-sky-300 text-sky-800",
  },
};

// ─── Inline formatDatewithTime (timezone-safe) ────────────────────────────────
function formatDatewithTime(inputDate) {
  if (inputDate == null) return "";
  const adjusted = inputDate.replace(/([+-]\d{2}:\d{2})$/, "");
  const date = new Date(adjusted);
  const offset = date.getTimezoneOffset() * 60000;
  const local = new Date(date.getTime() - offset);
  return local.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

// ─── Partner-facing display labels ───────────────────────────────────────────
// Overrides the shared state labels with clearer language for partners.
const PARTNER_DISPLAY_LABELS = {
  128: "Claimed by Customer",
  328: "Claimed by Customer",
  148: "Retrieved by Courier (RTS)",
  138: "Revoked by Courier",
};

// ─── Timeline builder ─────────────────────────────────────────────────────────
const buildTimeline = (pkg, stateLabels, hasIntegration) => {
  const events = [];
  const stateNo = Number(pkg.intPackageStateNo ?? pkg.packageStateNo);
  const isTerminal = [
    118, 128, 138, 141, 148, 150, 328, 350, 140, 130,
  ].includes(stateNo);
  const noOverstayStates = [118, 150, 350, 128, 328];
  const isPending = stateNo === 110 || stateNo === 320;
  const statusDt = pkg.statusDate ? new Date(pkg.statusDate).getTime() : null;
  const now = new Date();
  const pickupPassed = pkg.pickupByDate && new Date(pkg.pickupByDate) < now;

  if (pkg.packageDate) {
    events.push({
      label: stateNo >= 300 ? "Stored" : "Dropped off",
      date: pkg.packageDate,
      tier: "neutral",
    });
  }
  if (pkg.pickupByDate && !isTerminal && !pickupPassed) {
    events.push({ label: "Pending", date: pkg.packageDate, tier: "pending" });
  }
  if (pkg.pickupByDate && pickupPassed && !noOverstayStates.includes(stateNo)) {
    events.push({ label: "Overstay", date: pkg.pickupByDate, tier: "info" });
  }
  if (pkg.takebackOn && !isPending) {
    const passed = new Date(pkg.takebackOn) <= now;
    const dt = new Date(pkg.takebackOn).getTime();
    const isThisMilestone = hasIntegration ? stateNo === 140 : stateNo === 130;
    if (
      passed &&
      !isThisMilestone &&
      (!isTerminal || (statusDt && dt < statusDt))
    ) {
      events.push({
        label: hasIntegration ? "For RTS" : "For Take-Back",
        date: pkg.takebackOn,
        tier: "warning",
      });
    }
  }
  if (pkg.pulloutOn && !isPending) {
    const passed = new Date(pkg.pulloutOn) <= now;
    const dt = new Date(pkg.pulloutOn).getTime();
    const isThisMilestone = hasIntegration ? stateNo === 141 : false;
    if (
      passed &&
      !isThisMilestone &&
      (!isTerminal || (statusDt && dt < statusDt))
    ) {
      events.push({
        label: hasIntegration ? "RTS Overstay" : "Locker Clearing",
        date: pkg.pulloutOn,
        tier: "critical",
      });
    }
  }
  if (pkg.statusDate && isTerminal) {
    events.push({
      label:
        PARTNER_DISPLAY_LABELS[stateNo] ?? stateLabels[stateNo] ?? "Unknown",
      date: pkg.statusDate,
      tier: STATE_TIER_MAP[stateNo] ?? "negative",
    });
  }

  return events
    .map((e) => ({ ...e, date: formatDatewithTime(e.date) }))
    .reverse(); // latest first
};

// ─── Timeline step component ──────────────────────────────────────────────────
const TimelineStep = ({ event, isFirst, isLast }) => {
  const s = TIER_STYLES[event.tier] ?? TIER_STYLES.neutral;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center" style={{ width: 18 }}>
        <div
          className="relative mt-1 shrink-0"
          style={{ width: 14, height: 14 }}
        >
          <span className={`absolute inset-0 rounded-full ${s.dot}`} />
        </div>
        {!isLast && (
          <div
            className="w-px flex-1 mt-1 bg-gray-200"
            style={{ minHeight: 20 }}
          />
        )}
      </div>

      <div className={`flex-1 mb-3 rounded-xl border px-3 py-2.5 ${s.card}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-xs font-semibold ${s.text}`}>
                {event.label}
              </span>
              {isFirst && (
                <span className="text-[9px] font-bold bg-gray-800 text-white px-1.5 py-0.5 rounded-full">
                  LATEST
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{event.date}</div>
          </div>

          {event.tier === "warning" && (
            <span className="text-[9px] font-bold bg-orange-100 text-orange-800 border border-orange-300 px-2 py-0.5 rounded-full whitespace-nowrap">
              {isFirst ? "⚠ Fees starting" : "Fees started"}
            </span>
          )}
          {event.tier === "critical" && (
            <span className="text-[9px] font-bold bg-red-100 text-red-800 border border-red-300 px-2 py-0.5 rounded-full whitespace-nowrap">
              {isFirst ? "🔴 Fees escalating" : "Fees escalated"}
            </span>
          )}
          {event.tier === "success" && (
            <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
              ✓ No fees
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default function PackageHistoryPage() {
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions,
  );
  const { packageId, userAccountId } = useParams();

  const [filterPackage, setFilterPackage] = React.useState({});
  console.log({ filterPackage });
  const [partnerBusinessPartnerId, setPartnerBusinessPartnerId] =
    React.useState(null);
  const [openSurveillanceModal, setOpenSurveillanceModal] =
    React.useState(false);
  const [openRemoteOpenDoorModal, setOpenRemoteOpenDoorModal] =
    React.useState(false);
  const [openUpdateRecipientMobileModal, setOpenUpdateRecipientMobileModal] =
    React.useState(false);
  const [surveilanceImage, setSurveilanceImage] = React.useState([]);
  const [emailForm, setEmailForm] = React.useState({
    email: "",
    description: "",
  });
  const [openRefundDialog, setOpenRefundDialog] = React.useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [updatedPaymentData, setUpdatedPaymentData] = useState({});
  const [newRecipientMobile, setNewRecipientMobile] = React.useState("");
  const [proofPhoto, setProofPhoto] = React.useState({
    proofOfOwnership: "",
    idPhoto: "",
  });
  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });
  const [waitingModal, setWaitingModal] = React.useState(false);

  const operatorId = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")).operator_id
    : "";

  const filterPackageWithPayment = useQuery(
    NEW_PACKAGE_HISTORY_QUERY_WITH_PAYMENT,
    {
      variables: { operatorId, packageId },
      fetchPolicy: "no-cache",
    },
  );

  const { labels: stateLabels, hasIntegration } = usePackageStateLabel(
    partnerBusinessPartnerId,
  );

  const timeline = useMemo(
    () =>
      filterPackage.intPackageStateNo
        ? buildTimeline(filterPackage, stateLabels, hasIntegration)
        : [],
    [filterPackage, stateLabels, hasIntegration],
  );

  const tier = STATE_TIER_MAP[filterPackage.intPackageStateNo] ?? "neutral";
  const tierStyle = TIER_STYLES[tier] ?? TIER_STYLES.neutral;

  // ── API calls ────────────────────────────────────────────────────────────────
  const getCctvImages = () => {
    axios
      .post(
        `${process.env.REACT_APP_website_api}${API.package_camera_snapshot}`,
        {
          operator_id: operatorId,
          user_account_id: userAccountId,
          package_id: packageId,
        },
      )
      .then((response) => {
        try {
          setSurveilanceImage(
            response.data.d.snapshots.map((el) => ({
              image: el.image_url,
              timestamp: el.timestamp.toString(),
              camera_label: el.camera_label,
            })),
          );
        } catch {}
      });
  };

  const sendEmail = () => {
    const payload = {
      operator_id: operatorId,
      user_account_id: userAccountId,
      package_id: packageId,
      recipient_email: emailForm.email,
      email_message: emailForm.description,
    };
    try {
      axios
        .post(
          `${process.env.REACT_APP_website_api}${API.camera_snapshot_email}`,
          payload,
        )
        .then(() => {
          setValidation({
            icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
            title: "Success",
            message:
              "Your email containing the surveillance photo's package has been sent successfully.",
            open: true,
            actions: (
              <div className="w-full">
                <Button
                  onClick={() => setValidation({ open: false })}
                  value="OK"
                  className="p-2 w-full"
                />
              </div>
            ),
          });
          setOpenSurveillanceModal(false);
        });
    } catch {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Error",
        message: "An error occurred while sending.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full"
            />
          </div>
        ),
      });
    }
  };

  const updateAndSendPickupCode = (event) => {
    event.preventDefault();
    if (
      !proofPhoto.proofOfOwnership ||
      !proofPhoto.idPhoto ||
      !newRecipientMobile
    ) {
      return setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Empty fields/selection error",
        message:
          "Please provide photos of new recipient mobile number, proof of ownership and ID photo.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full"
            />
          </div>
        ),
      });
    }
    if (newRecipientMobile.length < 10) {
      return setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Validation error",
        message: "The mobile number should consist of exactly 10 characters.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full"
            />
          </div>
        ),
      });
    }

    setWaitingModal(true);
    setOpenUpdateRecipientMobileModal(false);
    axios
      .post(
        `${process.env.REACT_APP_website_api}${API.package_recipient_change}`,
        {
          operator_id: operatorId,
          package_id: packageId,
          old_recipient_mobile: `63${filterPackage.recipientMobile}`,
          new_recipient_mobile: `63${newRecipientMobile}`,
          proof_photo: proofPhoto.proofOfOwnership,
          proof_photo2: proofPhoto.idPhoto,
        },
      )
      .then(() => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
          title: "Success",
          message: "You have successfully updated and resent pick-up code!",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                  window.location.reload();
                }}
                value="OK"
                className="p-2 w-full"
              />
            </div>
          ),
        });
        setWaitingModal(false);
      })
      .catch(() => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: "Error",
          message:
            "An error occurred while updating and resending pick-up code.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full"
              />
            </div>
          ),
        });
        setWaitingModal(false);
      });
  };

  const resendPickUpCode = () => {
    setWaitingModal(true);
    axios
      .post(
        `${process.env.REACT_APP_website_api}${API.package_pickup_code_resend}`,
        {
          operator_id: operatorId,
          package_id: packageId,
          recipient_mobile: `63${filterPackage.recipientMobile}`,
        },
      )
      .then(() => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
          title: "Success",
          message: "You have successfully resent pick-up code!",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full"
              />
            </div>
          ),
        });
        setWaitingModal(false);
      })
      .catch(() => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: "Error",
          message: "Something went wrong.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full"
              />
            </div>
          ),
        });
        setWaitingModal(false);
      });
  };

  const handleValueChange = (e, field) => {
    if (field === "paymentCurrency") {
      setUpdatedPaymentData({ ...updatedPaymentData, [field]: e.value });
    } else {
      setUpdatedPaymentData({ ...updatedPaymentData, [field]: e.target.value });
    }
  };

  const handleSendEmailForSurveillance = (event) => {
    event.preventDefault();
    sendEmail();
  };

  React.useEffect(() => {
    if (filterPackageWithPayment.data) {
      const details = filterPackageWithPayment.data.getPackageDetails;
      if (!details?.length) return;
      const { package: pkg, payments } = details[0];
      setPartnerBusinessPartnerId(details[0].partnerBusinessPartnerId ?? null);
      setFilterPackage({
        userId: pkg.userAccount.user.userId,
        packageId: pkg.packageId,
        partnerAgentMobile: !pkg.partnerAgentMobile
          ? formatNumbers(pkg.senderMobile)
          : formatNumbers(pkg.partnerAgentMobile),
        senderMobile: formatNumbers(pkg.senderMobile),
        lockerName: pkg.locker.lockerName,
        recipientMobile: formatNumbers(pkg.recipientMobile),
        packageDate: pkg.packageDate,
        statusDate: pkg.statusDate,
        pickupByDate: pkg.pickupByDate,
        takebackOn: pkg.takebackOn,
        pulloutOn: pkg.pulloutOn,
        waybillNumber: pkg.waybillNumber || "(Storage)",
        packageStateNo: packageStates[pkg.packageStateNo],
        intPackageStateNo: Number(pkg.packageStateNo),
        agentId: pkg.partnerAgentId,
        service:
          pkg.packageStateNo >= 100 && pkg.packageStateNo < 200
            ? services[0]
            : pkg.packageStateNo >= 200 && pkg.packageStateNo < 300
              ? services[1]
              : services[2],
        currentStatus: packageStates[pkg.packageStateNo],
        senderName: pkg.senderName,
        lockerLocation: pkg.lockerLocation,
        door: pkg.door,
        locker: pkg.locker,
        packageDetected: pkg.packageDetected,
        transactionId: pkg.transactionId,
        payments: payments ?? [],
      });
    }
  }, [filterPackageWithPayment.data]);

  React.useEffect(() => {
    getCctvImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (filterPackageWithPayment.loading) {
    return (
      <>
        <Breadcrumbs items={breadcrumbItems} />
        <ContentContainer containerTitle="PACKAGE HISTORY">
          <div className="font-montserrat space-y-3">
            <div className="grid grid-cols-12 gap-4 mt-4">
              <div className="col-span-12 md:col-span-5 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} height="4rem" className="rounded-xl" />
                ))}
              </div>
              <div className="col-span-12 md:col-span-7 space-y-3">
                <Skeleton height="10rem" className="rounded-2xl" />
                <Skeleton height="5rem" className="rounded-2xl" />
                <Skeleton height="4rem" className="rounded-2xl" />
              </div>
            </div>
          </div>
        </ContentContainer>
      </>
    );
  }

  const isStorage = filterPackage.intPackageStateNo >= 300;
  const isTerminalForButtons = [328, 128, 138, 118].includes(
    filterPackage.intPackageStateNo,
  );
  // Use integration-aware label; fall back to flat map for partner states (300+)
  const currentStatus =
    stateLabels[filterPackage.intPackageStateNo] ??
    packageStates[filterPackage.intPackageStateNo] ??
    "Unknown";

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      <ContentContainer containerTitle="PACKAGE HISTORY">
        <div className="font-montserrat">
          {/* ── Dark header strip ── */}
          <div className="bg-primary-primaryDarkGray rounded-2xl px-5 py-4 mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">
                {isStorage ? "Package ID" : "Waybill Number"}
              </p>
              <p className="text-2xl font-extrabold text-primary-primaryYellow tracking-wide break-all">
                {isStorage
                  ? filterPackage.packageId
                  : filterPackage.waybillNumber}
              </p>
            </div>

            <div className="text-right">
              <p className="text-xs text-gray-400 mb-1">Current State</p>
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${tierStyle.badge}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tierStyle.dot}`} />
                {currentStatus}
              </span>
              <p className="text-[10px] text-gray-400 mt-1">
                as of {formatDatewithTime(filterPackage.statusDate)}
              </p>
            </div>

            {/* <div className="text-right">
              <p className="text-xs text-gray-400 mb-0.5">Service</p>
              <p className="text-sm font-bold text-primary-primaryYellow">
                {filterPackage.service}
              </p>
            </div> */}
          </div>

          {/* ── Main content grid ── */}
          <div className="grid grid-cols-12 gap-4">
            {/* ── Timeline ── */}
            <div className="col-span-12 md:col-span-5">
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-bold text-gray-800">
                    Package Timeline
                  </p>
                  <span className="text-[9px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Latest ↑
                  </span>
                </div>
                {timeline.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No events yet.</p>
                ) : (
                  timeline.map((event, idx) => (
                    <TimelineStep
                      key={idx}
                      event={event}
                      isFirst={idx === 0}
                      isLast={idx === timeline.length - 1}
                    />
                  ))
                )}
              </div>
            </div>

            {/* ── Details + Contact + Actions ── */}
            <div className="col-span-12 md:col-span-7 flex flex-col gap-4">
              {/* Package details */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <p className="text-sm font-bold text-gray-800 mb-3">
                  Package Details
                </p>
                <div className="space-y-0">
                  {[
                    {
                      label: isStorage ? "Package ID" : "Waybill Number",
                      value: isStorage
                        ? filterPackage.packageId
                        : filterPackage.waybillNumber,
                    },
                    {
                      label: "Locker Location",
                      value: filterPackage.lockerLocation
                        ? `${filterPackage.lockerLocation}${filterPackage.door?.doorAddress ? ` (${filterPackage.door.doorAddress})` : ""}`
                        : "—",
                    },
                    {
                      label: "Current State",
                      value: currentStatus,
                    },
                    {
                      label: "Last Updated",
                      value: formatDatewithTime(filterPackage.statusDate),
                    },
                    {
                      label: "Package Detection",
                      value:
                        filterPackage.packageDetected === -1 ||
                        filterPackage.packageDetected === "-1"
                          ? "No Package Detected"
                          : (PACKAGE_DETECTED[filterPackage.packageDetected] ??
                            "Unknown"),
                    },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="grid grid-cols-5 gap-2 py-2.5 border-b border-gray-100 last:border-0"
                    >
                      <span className="col-span-2 text-xs text-gray-400">
                        {label}
                      </span>
                      <span className="col-span-3 text-xs font-semibold text-gray-800 break-words">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact details */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <p className="text-sm font-bold text-gray-800 mb-3">
                  Contact Details
                </p>
                <div className="space-y-0">
                  {isStorage ? (
                    <div className="grid grid-cols-5 gap-2 py-2.5 border-b border-gray-100">
                      <span className="col-span-2 text-xs text-gray-400">
                        Sender Mobile
                      </span>
                      <span className="col-span-3 text-xs font-semibold text-gray-800">
                        {filterPackage.senderMobile || "—"}
                      </span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 gap-2 py-2.5 border-b border-gray-100">
                      <span className="col-span-2 text-xs text-gray-400">
                        Agent Mobile
                      </span>
                      <span className="col-span-3 text-xs font-semibold text-gray-800">
                        {filterPackage.partnerAgentMobile || "—"}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-5 gap-2 py-2.5">
                    <span className="col-span-2 text-xs text-gray-400">
                      Recipient Mobile
                    </span>
                    <span className="col-span-3 text-xs font-semibold text-gray-800">
                      {filterPackage.recipientMobile || "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <p className="text-sm font-bold text-gray-800 mb-3">Actions</p>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    value="View Surveillance Photos"
                    className="p-2 w-full"
                    onClick={() => setOpenSurveillanceModal(true)}
                  />
                  {!isTerminalForButtons && (
                    <>
                      <Button
                        value="Update Recipient Mobile"
                        disabled={
                          accessPermission.viewPackageHistory === 1 ||
                          accessPermission.viewPackageHistory === "1"
                        }
                        className="p-2 w-full"
                        onClick={() => setOpenUpdateRecipientMobileModal(true)}
                      />
                      <Button
                        value="Resend Pickup Code"
                        disabled={
                          accessPermission.viewPackageHistory === 1 ||
                          accessPermission.viewPackageHistory === "1"
                        }
                        className="p-2 w-full"
                        onClick={resendPickUpCode}
                      />
                      <Button
                        disabled
                        value="Remote Open Door"
                        className="p-2 w-full"
                        onClick={() => setOpenRemoteOpenDoorModal(true)}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Payment history ── */}
          <div className="mt-6">
            <div className="bg-primary-primaryDarkGray rounded-t-2xl px-5 py-4">
              <p className="text-xs text-gray-400 mb-0.5">Package</p>
              <p className="text-2xl font-extrabold text-primary-primaryYellow tracking-wide">
                {filterPackage.waybillNumber}
              </p>
              <p className="text-sm font-bold text-white mt-1">
                Package Payment History
              </p>
            </div>

            <div className="border border-t-0 rounded-b-2xl overflow-hidden shadow-sm">
              <DataTable
                value={filterPackage.payments ?? []}
                stripedRows
                emptyMessage="No payment records found."
              >
                <Column
                  field="paymentStatus"
                  header="Payment Status"
                  alignHeader="center"
                  className="text-center w-[180px] border-r-2 break-words"
                  headerClassName="bg-primary-primaryYellow text-center border-r-2"
                  body={(rowData) => (
                    <Tag
                      className={
                        rowData.paymentStatus === "Success"
                          ? "bg-green-500 border-0 text-white"
                          : rowData.paymentStatus === "Pending"
                            ? "bg-yellow-500 border-0 text-white"
                            : "bg-red-500 border-0 text-white"
                      }
                    >
                      {rowData.paymentStatus}
                    </Tag>
                  )}
                />
                <Column
                  field="duplicate"
                  header="Valid for refund?"
                  className="text-center w-[200px] break-words border-r-2"
                  alignHeader="center"
                  headerClassName="bg-primary-primaryYellow text-center border-r-2"
                  body={(rowData) => (
                    <Tag
                      className={
                        rowData.duplicate === true ||
                        rowData.duplicate === "true"
                          ? "bg-green-500 border-0 text-white"
                          : "bg-red-500 border-0 text-white"
                      }
                    >
                      {rowData.duplicate === true ||
                      rowData.duplicate === "true"
                        ? "Yes"
                        : "No"}
                    </Tag>
                  )}
                />
                <Column
                  field="paymentAmount"
                  alignHeader="center"
                  header="Payment Amount"
                  className="text-center max-w-[200px] break-words border-r-2"
                  headerClassName="bg-primary-primaryYellow text-center border-r-2"
                  body={(rowData) => (
                    <div>
                      {rowData.paymentCurrency === "PHP"
                        ? `₱ ${rowData.paymentAmount}`
                        : rowData.paymentAmount}
                    </div>
                  )}
                />
                <Column
                  field="paymentCurrency"
                  alignHeader="center"
                  header="Payment Currency"
                  className="text-center max-w-[200px] break-words border-r-2"
                  headerClassName="bg-primary-primaryYellow text-center border-r-2"
                  body={(rowData) => (
                    <div>
                      {rowData.paymentCurrency === "PHP" ? "PHP" : "MBOX COINS"}
                    </div>
                  )}
                />
                <Column
                  field="paymentDate"
                  sortable
                  alignHeader="center"
                  className="text-center max-w-[200px] break-words border-r-2"
                  headerClassName="bg-primary-primaryYellow text-center border-r-2"
                  header="Payment Date"
                  body={(rowData) => (
                    <div>{formatDateTime(rowData.paymentDate)}</div>
                  )}
                />
                <Column
                  headerClassName="bg-primary-primaryYellow text-center"
                  alignHeader="center"
                  header=""
                  className="text-center"
                  body={(rowData) => (
                    <div className="flex gap-3 items-center justify-center">
                      <PrimereactButton
                        hidden={
                          accessPermission.viewPackageHistory === 1 ||
                          accessPermission.viewPackageHistory === "1"
                        }
                        className="bg-primary-primaryYellowLight text-black border-0 px-4 py-1 text-sm font-semibold hover:bg-primary-primaryYellow"
                        onClick={() => {
                          setSelectedRowData(rowData);
                          setOpenRefundDialog(true);
                        }}
                        size="small"
                      >
                        Request Refund
                      </PrimereactButton>
                    </div>
                  )}
                />
              </DataTable>
            </div>
          </div>

          {waitingModal && (
            <div className="mt-4">
              <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
              <p className="text-center text-sm mt-2">Sending pickup code...</p>
            </div>
          )}
        </div>
      </ContentContainer>

      <ViewSurveillancePhotosModal
        open={openSurveillanceModal}
        packageState={filterPackage.intPackageStateNo}
        packageId={filterPackage.packageId}
        disabled={
          accessPermission.viewPackageHistory === 1 ||
          accessPermission.viewPackageHistory === "1"
        }
        images={surveilanceImage}
        waybillNumber={filterPackage.waybillNumber}
        location={filterPackage.lockerLocation}
        sendEmailForSurveillance={handleSendEmailForSurveillance}
        form={emailForm}
        setForm={setEmailForm}
        onClose={() => setOpenSurveillanceModal(false)}
      />
      <RemoteOpenDoorModal
        open={openRemoteOpenDoorModal}
        onClose={() => setOpenRemoteOpenDoorModal(false)}
      />
      <UpdateRecipientMobileModal
        open={openUpdateRecipientMobileModal}
        packageState={filterPackage.intPackageStateNo}
        packageId={filterPackage.packageId}
        waybillNumber={filterPackage.waybillNumber}
        onClose={() => {
          setNewRecipientMobile("");
          setProofPhoto({ proofOfOwnership: "", idPhoto: "" });
          setOpenUpdateRecipientMobileModal(false);
        }}
        setRecipientMobile={setNewRecipientMobile}
        setProofPhoto={setProofPhoto}
        updateAndSendPickupCode={updateAndSendPickupCode}
      />
      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
      <RefundDialog
        visible={openRefundDialog}
        setUpdatedPaymentData={setUpdatedPaymentData}
        onClose={() => {
          setOpenRefundDialog(false);
          setSelectedRowData({});
          setUpdatedPaymentData({});
        }}
        dropdownOptions={[
          { label: "PHP", value: 1 },
          { label: "MBOX COINS", value: 2 },
        ]}
        paymentData={selectedRowData}
        packageData={filterPackage}
        onChange={handleValueChange}
        updatedPaymentData={updatedPaymentData}
      />
    </>
  );
}
